import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import {
  makeSelectCartItemById,
  makeSelectMaterialPricesForCartItem,
  selectProductMaterialPrices,
  selectSelectedMaterialFromStore,
} from '../../redux/';
import { makeSelectMaterialImagesByProductId } from '../../redux/createSelector/createMaterialSelector';
import type { MaterialSelectorCartItem, MaterialSelectorOption, MaterialSelectorProps } from '../interfaces';
import { getSelectedMaterialName, normalizeMaterials } from './materialSelector.utils';

const EMPTY_ARRAY: MaterialSelectorOption[] = [];
const selectEmptyArray = () => EMPTY_ARRAY;

export const useMaterialSelectorData = ({
  value,
  itemId,
  productId,
  dataSource,
  materials,
}: MaterialSelectorProps) => {
  const { pathname } = useLocation();
  const isCartPage = dataSource === 'cart' || pathname.startsWith('/cart');
  const isProductDetailPage = /^\/products\/[^/]+$/.test(pathname);
  const userId = useAppSelector((state) => state.user.user?.id);
  const materialSelector = useMemo(
    () => (productId ? makeSelectMaterialImagesByProductId(productId) : selectEmptyArray),
    [productId]
  );
  const cartItemSelector = useMemo(
    () => (itemId ? makeSelectCartItemById(itemId) : () => undefined),
    [itemId]
  );
  const materialPricesSelector = useMemo(
    () => (itemId ? makeSelectMaterialPricesForCartItem(itemId) : selectEmptyArray),
    [itemId]
  );
  const materialImagesFromStore = useAppSelector(materialSelector) as MaterialSelectorOption[];
  const cartItem = useAppSelector(cartItemSelector) as MaterialSelectorCartItem | undefined;
  const materialPricesFromCart = useAppSelector(materialPricesSelector) as MaterialSelectorOption[];
  const materialPricesFromProduct = useAppSelector(selectProductMaterialPrices) as MaterialSelectorOption[];
  const selectedMaterialFromStore = useAppSelector((state) =>
    isCartPage && cartItem ? cartItem.selectedMaterial || '' : selectSelectedMaterialFromStore(state)
  );
  const normalizedMaterials = useMemo(() => normalizeMaterials(materials), [materials]);
  const materialPrices = useMemo(() => {
    if (normalizedMaterials) return normalizedMaterials;
    if (isCartPage && itemId) return materialPricesFromCart;
    if (productId) return materialImagesFromStore;
    return materialPricesFromProduct;
  }, [normalizedMaterials, isCartPage, itemId, productId, materialPricesFromCart, materialPricesFromProduct, materialImagesFromStore]);
  const selectedMaterial = useMemo(
    () => getSelectedMaterialName(value, selectedMaterialFromStore, materialPrices, isProductDetailPage || isCartPage),
    [value, selectedMaterialFromStore, materialPrices, isProductDetailPage, isCartPage]
  );
  return {
    userId,
    cartItem,
    isCartPage,
    isProductDetailPage,
    materialPrices,
    selectedMaterial,
  };
};
